/**
 * /payroll-l3/working-rules — Admin page for CBAWorkingRule rows.
 *
 * Shows the working_rules engine's input data: holiday lists, show-up
 * pay rules, apprentice-ratio rules, etc. Pick a CBA, see its rules,
 * add/edit/delete.
 *
 * The engine itself reads from these rows during pay-run processing —
 * see ``app/modules/construction/union/services/working_rules.py`` and
 * ``services/apprentice_ratio_engine.py``.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l3/working-rules")({
  component: WorkingRulesPage,
});

interface CBARow {
  id: string;
  trade_name: string;
  local_id: string;
  effective_start: string;
  effective_end: string | null;
  is_active: boolean;
}
interface LocalRow {
  id: string;
  local_number: string;
  name: string;
}

interface WorkingRule {
  id: string;
  cba_id: string;
  rule_type: string;
  rule_data: Record<string, unknown>;
  notes: string | null;
  effective_start: string | null;
  effective_end: string | null;
  is_draft: boolean;
}

const RULE_TYPES = [
  {
    value: "holiday_list",
    label: "Holiday list",
    desc: "Dates that pay at the CBA's holiday multiplier.",
    template: { dates: ["YYYY-MM-DD"] },
  },
  {
    value: "apprentice_ratio",
    label: "Apprentice ratio",
    desc: "Required apprentice-to-journeyman ratio + exemption rules.",
    template: {
      required_ratio: "0.25",
      first_apprentice_exemption: false,
      max_first_apprentice_hours: 0,
    },
  },
  {
    value: "show_up_pay",
    label: "Show-up pay (notes only)",
    desc: "Show-up pay HOURS live on each CBAWageRate row. Use this rule type for narrative notes about reporting-pay variants.",
    template: { notes: "Show-up minimum is set per classification on the wage-rate row." },
  },
  {
    value: "travel_zone",
    label: "Travel zone",
    desc: "Travel-pay rules. Engine support is roadmap; rules entered now are honored when the engine ships.",
    template: { free_zone_miles: 30, rate_per_mile: "0.50" },
  },
  {
    value: "other",
    label: "Other / freeform",
    desc: "Catch-all for CBA provisions not covered above.",
    template: {},
  },
] as const;

function ruleLabel(t: string): string {
  return RULE_TYPES.find((r) => r.value === t)?.label ?? t;
}

/** Render rule_data as a human-readable summary instead of raw JSON. */
function RuleDataSummary({ rule_type, rule_data }: { rule_type: string; rule_data: Record<string, unknown> }) {
  // Holiday list: show count + first few names
  if (rule_type === "holiday_list") {
    const holidays = rule_data.holidays as Array<{ name?: string; date?: string } | string> | undefined;
    const dates = rule_data.dates as string[] | undefined;
    if (holidays && holidays.length > 0) {
      const names = holidays
        .map((h) => (typeof h === "string" ? h : h.name ?? h.date))
        .filter(Boolean);
      return (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {names.length} holiday{names.length !== 1 ? "s" : ""}
          </Typography>
          <Typography variant="caption">
            {names.slice(0, 4).join(" · ")}{names.length > 4 ? ` + ${names.length - 4} more` : ""}
          </Typography>
        </Box>
      );
    }
    if (dates && dates.length > 0) {
      return (
        <Typography variant="caption">
          {dates.length} date{dates.length !== 1 ? "s" : ""}: {dates.slice(0, 3).join(", ")}{dates.length > 3 ? "…" : ""}
        </Typography>
      );
    }
  }

  // Apprentice ratio
  if (rule_type === "apprentice_ratio") {
    const ratio = rule_data.required_ratio ?? rule_data.ratio;
    const exemption = rule_data.first_apprentice_exemption;
    return (
      <Box>
        {ratio != null && (
          <Typography variant="caption" display="block">
            Ratio: <strong>{String(ratio)}</strong> apprentice per journeyman
          </Typography>
        )}
        {exemption != null && (
          <Typography variant="caption" color="text.secondary" display="block">
            First-apprentice exemption: {exemption ? "yes" : "no"}
          </Typography>
        )}
      </Box>
    );
  }

  // Travel zone
  if (rule_type === "travel_zone") {
    const miles = rule_data.free_zone_miles;
    const rate = rule_data.rate_per_mile;
    return (
      <Typography variant="caption">
        Free zone: {miles != null ? `${miles} mi` : "—"}
        {rate != null ? ` · $${rate}/mi after` : ""}
      </Typography>
    );
  }

  // Show-up pay or other: show description/notes/restriction fields if present
  const summaryField = rule_data.description ?? rule_data.notes ?? rule_data.restriction
    ?? rule_data.scope ?? rule_data.summary;
  if (summaryField) {
    return (
      <Typography variant="caption" sx={{ maxWidth: 420, display: "block" }}>
        {String(summaryField).slice(0, 200)}{String(summaryField).length > 200 ? "…" : ""}
      </Typography>
    );
  }

  // Fallback: compact key=value list (no raw JSON)
  const entries = Object.entries(rule_data).filter(([, v]) => v != null && v !== "");
  if (entries.length === 0) return <Typography variant="caption" color="text.secondary">—</Typography>;
  return (
    <Box>
      {entries.slice(0, 4).map(([k, v]) => (
        <Typography key={k} variant="caption" display="block">
          <Box component="span" sx={{ color: "text.secondary" }}>{k}:</Box>{" "}
          {typeof v === "object" ? JSON.stringify(v).slice(0, 60) : String(v).slice(0, 80)}
        </Typography>
      ))}
      {entries.length > 4 && (
        <Typography variant="caption" color="text.secondary">+{entries.length - 4} more fields</Typography>
      )}
    </Box>
  );
}

function fmtDate(iso: string | null): string {
  return iso ? iso : "—";
}

function WorkingRulesPage() {
  const qc = useQueryClient();
  const [selectedCba, setSelectedCba] = useState<string>("");
  const [editing, setEditing] = useState<WorkingRule | null>(null);
  const [creating, setCreating] = useState(false);

  // CBA list
  const cbasQ = useQuery({
    queryKey: ["payroll-l3", "cbas", "for-working-rules"],
    queryFn: () => api<CBARow[]>("/api/payroll-l3/cbas"),
  });
  const localsQ = useQuery({
    queryKey: ["payroll-l3", "locals", "for-working-rules"],
    queryFn: () => api<LocalRow[]>("/api/payroll-l3/locals"),
  });

  const localById = useMemo(() => {
    const m = new Map<string, LocalRow>();
    (localsQ.data ?? []).forEach((l) => m.set(l.id, l));
    return m;
  }, [localsQ.data]);

  // Default to first CBA when list loads
  if (!selectedCba && cbasQ.data && cbasQ.data.length > 0) {
    setSelectedCba(cbasQ.data[0].id);
  }

  // Rules for selected CBA
  const rulesQ = useQuery({
    queryKey: ["payroll-l3", "working-rules", selectedCba],
    queryFn: () =>
      api<WorkingRule[]>(`/api/payroll-l3/cbas/${selectedCba}/working-rules`),
    enabled: !!selectedCba,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      api(`/api/payroll-l3/working-rules/${id}`, { method: "DELETE" }),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: ["payroll-l3", "working-rules", selectedCba],
      }),
  });

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" fontWeight={600}>
          CBA Working Rules
        </Typography>
        <HelpHint
          docPath="payroll-l3/working-rules.md"
          label="Working rules guide"
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Operational rules attached to a CBA — holiday lists, show-up pay
        notes, apprentice ratios, travel zones. The pay-run engine reads
        these during processing.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <TextField
          select
          size="small"
          label="CBA"
          value={selectedCba}
          onChange={(e) => setSelectedCba(e.target.value)}
          sx={{ minWidth: 360 }}
        >
          {(cbasQ.data ?? []).map((c) => {
            const local = localById.get(c.local_id);
            const localLabel = local
              ? `Local ${local.local_number}`
              : c.local_id.slice(0, 8);
            return (
              <MenuItem key={c.id} value={c.id}>
                {c.trade_name} — {localLabel}
                {c.effective_end ? ` (ends ${c.effective_end})` : ""}
              </MenuItem>
            );
          })}
        </TextField>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreating(true)}
          disabled={!selectedCba}
        >
          Add rule
        </Button>
      </Stack>

      {rulesQ.isLoading && <CircularProgress size={24} />}
      {rulesQ.error && <Alert severity="error">Failed to load rules.</Alert>}

      {rulesQ.data && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Effective</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rulesQ.data.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Chip
                      label={ruleLabel(r.rule_type)}
                      size="small"
                      color="primary"
                    />
                    {r.is_draft && (
                      <Chip label="draft" size="small" />
                    )}
                  </Stack>
                </TableCell>
                <TableCell sx={{ maxWidth: 460 }}>
                  <RuleDataSummary rule_type={r.rule_type} rule_data={r.rule_data} />
                </TableCell>
                <TableCell>
                  {fmtDate(r.effective_start)} → {fmtDate(r.effective_end)}
                </TableCell>
                <TableCell sx={{ maxWidth: 220 }}>
                  <Typography variant="body2" noWrap>
                    {r.notes ?? "—"}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => setEditing(r)}
                    aria-label="edit"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (confirm(`Delete this ${ruleLabel(r.rule_type)} rule?`)) {
                        deleteMut.mutate(r.id);
                      }
                    }}
                    aria-label="delete"
                    disabled={deleteMut.isPending}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {rulesQ.data.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  align="center"
                  sx={{ color: "text.secondary", py: 4 }}
                >
                  No working rules yet for this CBA.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {(creating || editing) && selectedCba && (
        <RuleDialog
          cbaId={selectedCba}
          rule={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </Box>
  );
}

function RuleDialog({
  cbaId,
  rule,
  onClose,
}: {
  cbaId: string;
  rule: WorkingRule | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = rule !== null;
  const [ruleType, setRuleType] = useState<string>(
    rule?.rule_type ?? "holiday_list",
  );
  const [ruleDataText, setRuleDataText] = useState<string>(() => {
    if (rule) return JSON.stringify(rule.rule_data, null, 2);
    const tpl = RULE_TYPES.find((r) => r.value === "holiday_list")?.template ?? {};
    return JSON.stringify(tpl, null, 2);
  });
  const [notes, setNotes] = useState<string>(rule?.notes ?? "");
  const [effectiveStart, setEffectiveStart] = useState<string>(
    rule?.effective_start ?? "",
  );
  const [effectiveEnd, setEffectiveEnd] = useState<string>(
    rule?.effective_end ?? "",
  );
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: async () => {
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(ruleDataText);
      } catch (e) {
        throw new Error("Rule data must be valid JSON");
      }
      const body = {
        rule_type: ruleType,
        rule_data: parsed,
        notes: notes || null,
        effective_start: effectiveStart || null,
        effective_end: effectiveEnd || null,
      };
      if (isEdit && rule) {
        return api(`/api/payroll-l3/working-rules/${rule.id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
      }
      return api(`/api/payroll-l3/cbas/${cbaId}/working-rules`, {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["payroll-l3", "working-rules", cbaId],
      });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  function applyTemplate(newType: string) {
    setRuleType(newType);
    if (!isEdit) {
      const tpl = RULE_TYPES.find((r) => r.value === newType)?.template ?? {};
      setRuleDataText(JSON.stringify(tpl, null, 2));
    }
  }

  const tpl = RULE_TYPES.find((r) => r.value === ruleType);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? "Edit working rule" : "Add working rule"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            select
            label="Rule type"
            value={ruleType}
            onChange={(e) => applyTemplate(e.target.value)}
            disabled={isEdit}
          >
            {RULE_TYPES.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>
          {tpl && (
            <Typography variant="caption" color="text.secondary">
              {tpl.desc}
            </Typography>
          )}
          <TextField
            label="Rule data (JSON)"
            multiline
            minRows={6}
            value={ruleDataText}
            onChange={(e) => setRuleDataText(e.target.value)}
            sx={{ fontFamily: "monospace" }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Effective start"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={effectiveStart}
              onChange={(e) => setEffectiveStart(e.target.value)}
              fullWidth
            />
            <TextField
              label="Effective end (optional)"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={effectiveEnd}
              onChange={(e) => setEffectiveEnd(e.target.value)}
              fullWidth
            />
          </Stack>
          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            setError(null);
            saveMut.mutate();
          }}
          disabled={saveMut.isPending}
        >
          {isEdit ? "Save changes" : "Create rule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
